import { FC, useContext } from 'react'
import clsx from 'clsx'
import { format } from 'date-fns'
import gql from 'graphql-tag'
import take from 'lodash/take'
import Image from 'next/image'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import {
  ActivityRecruitType,
  ActivityTableRow_ActivityFragment,
  ActivityTableRow_CurrentUserFragment,
  JobType,
} from 'generated/graphql'
import { RecruitListScreenContext } from '@app.feature/recruitList/context'
import { extractInternTypesName } from '@app.feature/recruitList/module/extractInternTypesName'
import { getAddressesText } from '@app.feature/recruitList/module/getAddressesText'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import { RECRUIT_LOGO_IMAGE_SIZE } from 'app.constants/recruitImageSize'
import ActivityScrap from 'components/ActivityScrap'
import Link from 'components/Link'
import BookMark from 'svgIcons/BookMark'
import { optimizeImageUrl, imageLoader } from 'utils/optimizeImageUrl'
import { getJobType } from 'utils/utils'
import {
  getCategoriesText,
  getRegionsText,
  getViewCount,
} from '../../../../../module'
import ActivityTableRowLoading from './ActivityTableRowLoading.desktop'

gql`
  fragment ActivityTableRow_currentUser on User {
    ...ActivityScrap_currentUser
  }
  fragment ActivityTableRow_activity on Activity {
    id
    type
    isScrapped
    title
    jobTypes
    recruitInformations {
      id
      jobType
      internTypes {
        id
        name
      }
    }
    recruitType
    recruitScale
    organizationName
    recruitCloseAt
    viewCount
    logoImage {
      id
      url
    }
    categories {
      id
      name
    }
    regions {
      id
      name
    }
    regionDistricts {
      id
      name
      region {
        id
        name
      }
    }
    addresses {
      address
      bcode
      bname
      detailAddress
      id
      isAbroad
      isPossibleWorkingFromHome
      sido
      sigungu
      sigunguCode
      zoneCode
    }
    ...ActivityScrap_activity
  }
`

interface ActivityTableRowProps {
  className?: string
  isLoading?: boolean
  activity?: ActivityTableRow_ActivityFragment
  currentUser?: ActivityTableRow_CurrentUserFragment | null
  isRecommend?: boolean
  onClick?: () => void
}

const ActivityTableRow: FC<ActivityTableRowProps> = (props) => {
  const {
    className = '',
    isLoading,
    activity,
    currentUser,
    isRecommend,
    onClick,
  } = props
  const router = useRouter()
  const isWebview = useIsWebview()
  const { state } = useContext(RecruitListScreenContext)

  if (isLoading) {
    return <ActivityTableRowLoading />
  }

  const handleClickOrganization = () => {
    if (activity?.manager?.channel) {
      router.push(
        getWebviewHref(`/channel/${activity.manager.channel.url}`, isWebview),
      )
    }
  }

  const isIntern =
    router.pathname.includes('intern') ||
    state.filterBy.jobTypes?.includes(JobType.INTERN)
  const categoriesText = getCategoriesText(activity?.categories)
  const viewCount = getViewCount(activity?.viewCount || 0)
  const regionsText = getRegionsText(
    activity?.regionDistricts,
    activity?.regions,
  )
  const addressesText = getAddressesText(activity?.addresses ?? [])

  const imageUrl = activity?.logoImage?.url || ''
  const internTypesCount = activity?.recruitInformations
    .map((info) => {
      return info?.internTypes.length ?? 0
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0)

  return (
    <StyledWrapper
      className={className}
      isCursorPointer={
        !!(
          activity?.manager?.channel !== undefined &&
          activity?.manager?.channel !== null
        )
      }
      data-testid="activity-table-row"
      data-activityid={activity?.id}
    >
      <td>
        <div className="item-recruit-company" onClick={handleClickOrganization}>
          <div className="company-thumb">
            <Image
              loader={imageLoader}
              height={RECRUIT_LOGO_IMAGE_SIZE}
              width={RECRUIT_LOGO_IMAGE_SIZE}
              alt={activity?.title || ''}
              src={optimizeImageUrl({
                url: imageUrl,
                width: 'auto',
                height: RECRUIT_LOGO_IMAGE_SIZE,
              })}
              quality={100}
              objectFit="contain"
            />
          </div>
          <p className="company-name">{activity?.organizationName}</p>
        </div>
      </td>
      <td>
        <div className="item-recruit-name">
          <Link
            className="recruit-link"
            href={'/activity/[id]'}
            target="_blank"
            as={`/activity/${activity?.id}`}
            naked
            onClick={() => {
              onClick && onClick()
            }}
          >
            <div className="recruit-name-container">
              {isRecommend && (
                <div className="recommend-tag-container">
                  <span className="recommend-text">추천</span>
                </div>
              )}
              <p className="recruit-name">{activity?.title}</p>
            </div>
            <p className="recruit-category">{categoriesText}</p>
          </Link>
        </div>
      </td>
      <td>
        <div className="item-recruit-type">
          <>
            {isIntern ? (
              <p className="short-info-typo">
                {extractInternTypesName({
                  internTypesCount,
                  activity,
                })}
              </p>
            ) : (
              <p className="short-info-typo">
                {activity?.jobTypes?.length
                  ? getJobType(take(activity.jobTypes, 2))
                  : '-'}
              </p>
            )}
          </>
        </div>
      </td>
      <td>
        <div className="item-location">
          <p className="short-info-typo">
            {activity?.addresses && activity?.addresses.length >= 1
              ? addressesText
              : regionsText}
          </p>
        </div>
      </td>
      <td>
        <div className="item-recruit-close">
          <p className="short-info-typo">
            {activity?.recruitType
              ? {
                  [ActivityRecruitType.NORMAL]: activity?.recruitCloseAt
                    ? `~ ${format(new Date(activity.recruitCloseAt), 'MM.dd')}`
                    : '-',
                  [ActivityRecruitType.ALWAYS]: '상시채용',
                  [ActivityRecruitType.ASAP]: '채용 시 마감',
                  [ActivityRecruitType.SCHEDULED]: '예정',
                }[activity.recruitType]
              : '-'}
          </p>
        </div>
      </td>
      <td>
        <div className="item-view-count">
          <p className="short-info-typo">{viewCount}</p>
        </div>
      </td>
      <td>
        <div className="item-button-scrap">
          <div>
            <ActivityScrap activity={activity} currentUser={currentUser}>
              {({ scrapActivity, loading, scrapCount }) => {
                return (
                  <>
                    <button
                      onClick={scrapActivity}
                      className="button-scrap"
                      disabled={loading}
                      aria-label="스크랩하기"
                    >
                      <BookMark
                        className="icon-bookmark"
                        isOn={activity?.isScrapped}
                        strokeType="#666666"
                      />
                    </button>
                    <p
                      className={clsx('short-info-typo', {
                        scrapped: activity?.isScrapped,
                      })}
                    >
                      {scrapCount}
                    </p>
                  </>
                )
              }}
            </ActivityScrap>
          </div>
        </div>
      </td>
    </StyledWrapper>
  )
}

export default ActivityTableRow

type TStyledProps = {
  isCursorPointer: boolean
}

const StyledWrapper = styled.tr<TStyledProps>`
  ${({ theme, isCursorPointer }) => css`
    width: 100%;
    background-color: #ffffff;

    .item-recruit-company {
      height: 100%;
      display: flex;
      align-items: center;
      padding-left: 20px;
      cursor: ${isCursorPointer && 'pointer'};

      .company-thumb {
        width: 60px;
        height: 60px;
        margin-right: 20px;
        flex-shrink: 0;
      }

      .company-name {
        font-size: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
        font-weight: 300;
        color: ${theme.color.text_1};
      }
    }

    .item-recruit-name {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;

      .recruit-link {
        width: 100%;

        &:hover {
          .recommend-text,
          .recruit-name,
          .recruit-category {
            text-decoration: underline;
          }
        }

        .recruit-name-container {
          display: flex;

          .recommend-tag-container {
            display: flex;
            padding: 2px;
            border: 1px solid #ff0066;
            border-radius: 3px;
            margin-right: 5px;
            flex-shrink: 0;
            height: 100%;

            .recommend-text {
              color: ${theme.color.secondary_1};
              font-size: 11px;
            }
          }

          .recruit-name {
            font-size: 16px;
            color: ${theme.color.text_1};
            line-height: 1.2em;
            white-space: pre-wrap;
            font-weight: 500;
            letter-spacing: -0.6px;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: left;
          }
        }

        .recruit-category {
          max-width: 270px;
          height: 24px;
          font-size: 16px;
          color: ${theme.color.text_3};
          line-height: 24px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
          font-weight: 400;
        }
      }
    }

    .item-recruit-type,
    .item-location,
    .item-recruit-close,
    .item-view-count {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .short-info-typo {
        font-size: 16px;
        color: ${theme.color.text_1};
        white-space: nowrap;
        letter-spacing: -0.6px;
        line-height: 1.5;
        font-weight: 400;
        text-overflow: ellipsis;
        text-align: center;
        overflow: hidden;
        padding: 0 1px;
      }
    }

    .item-button-scrap {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .button-scrap {
        height: 21px;
        color: ${theme.line.line_3};
        border: 0;
        background-color: transparent;
        overflow: visible;
        font-size: 1.5rem;
        text-align: center;
        padding: 12px 12px 0;
        transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        cursor: pointer;

        .icon-bookmark {
          color: #01a0ff;
          width: 15px;
        }
      }

      .short-info-typo {
        font-size: 16px;
        color: ${theme.color.text_3};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: center;
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: -0.6px;

        &.scrapped {
          color: ${theme.color.primary_2};
        }
      }
    }
  `}
`
